import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, Calendar, ArrowRight, Check } from 'lucide-react';
import { submitContact } from '../lib/api';

const SERVICES = ['SaaS', '3D Website', 'Mobile App', 'Digital Marketing', 'Other'];
const BUDGETS = ['< $5k', '$5k – $15k', '$15k – $50k', '$50k+'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', service: '', budget: '', message: '', website: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Please tell us your name.';
    if (!form.email.trim()) err.email = 'We need an email to reply.';
    else if (!EMAIL_RE.test(form.email)) err.email = 'That email looks off — mind checking?';
    if (!form.message.trim()) err.message = 'A quick note helps us prepare.';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    try {
      await submitContact(form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-40 bg-bone" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-graphite">Start a project</span>
          <h2 className="serif tracking-tighter text-[13vw] md:text-[7vw] leading-[0.9] mt-4">
            Let's build <span className="italic">something</span>.
          </h2>
          <p className="font-sans text-base text-graphite mt-8 max-w-sm">
            We reply within 1 business day — usually much faster. Prefer to talk? Use any channel below.
          </p>

          <div className="mt-10 space-y-4">
            <a href="mailto:meet@raufmedia.com" className="flex items-center gap-3 group" data-testid="contact-email-link">
              <span className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center group-hover:border-signal group-hover:text-signal transition-colors duration-300">
                <Mail size={16} />
              </span>
              <span className="font-sans text-sm">meet@raufmedia.com</span>
            </a>
            <a href="https://wa.me/16043689865" className="flex items-center gap-3 group" data-testid="contact-whatsapp-link">
              <span className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center group-hover:border-signal group-hover:text-signal transition-colors duration-300">
                <Phone size={16} />
              </span>
              <span className="font-sans text-sm">+1 604 368 9865 · WhatsApp</span>
            </a>
            <a href="#" className="flex items-center gap-3 group" data-testid="contact-booking-link">
              <span className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center group-hover:border-signal group-hover:text-signal transition-colors duration-300">
                <Calendar size={16} />
              </span>
              <span className="font-sans text-sm">Book a free 20-min call</span>
            </a>
          </div>
        </div>

        <div className="md:col-span-7">
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full flex flex-col justify-center items-start"
                data-testid="contact-success"
              >
                <span className="w-16 h-16 rounded-full bg-signal text-bone flex items-center justify-center mb-8">
                  <Check size={28} />
                </span>
                <h3 className="serif text-5xl md:text-6xl tracking-tight leading-none">
                  Message <span className="italic">received.</span>
                </h3>
                <p className="font-sans text-base text-graphite mt-6 max-w-md">
                  We reply within 1 business day — usually much faster. Check your inbox for a confirmation.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                noValidate
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
                data-testid="contact-form"
              >
                {/* honeypot */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set('website')}
                  className="hidden"
                  aria-hidden="true"
                  data-testid="hp-website"
                />

                <div>
                  <input
                    className="brutal-input"
                    placeholder="Your name *"
                    value={form.name}
                    onChange={set('name')}
                    data-testid="contact-name"
                  />
                  {errors.name && <p className="text-signal font-sans text-sm mt-2" data-testid="error-name">{errors.name}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    className="brutal-input"
                    placeholder="Email address *"
                    value={form.email}
                    onChange={set('email')}
                    data-testid="contact-email"
                  />
                  {errors.email && <p className="text-signal font-sans text-sm mt-2" data-testid="error-email">{errors.email}</p>}
                </div>

                <input
                  className="brutal-input"
                  placeholder="Company (optional)"
                  value={form.company}
                  onChange={set('company')}
                  data-testid="contact-company"
                />

                <div className="grid md:grid-cols-2 gap-8">
                  <select className="brutal-input" value={form.service} onChange={set('service')} data-testid="contact-service">
                    <option value="">Service interest</option>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select className="brutal-input" value={form.budget} onChange={set('budget')} data-testid="contact-budget">
                    <option value="">Budget range (optional)</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                <div>
                  <textarea
                    className="brutal-input resize-none"
                    rows={3}
                    placeholder="Tell us about your project *"
                    value={form.message}
                    onChange={set('message')}
                    data-testid="contact-message"
                  />
                  {errors.message && <p className="text-signal font-sans text-sm mt-2" data-testid="error-message">{errors.message}</p>}
                </div>

                {status === 'error' && (
                  <p className="text-signal font-sans text-sm" data-testid="contact-error">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group inline-flex items-center gap-4 px-10 py-5 rounded-full bg-obsidian text-bone font-sans text-sm uppercase tracking-[0.2em] hover:bg-signal transition-colors duration-500 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {status === 'loading' ? 'Sending…' : 'Send message'}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
